import { RegimeModel } from 'app/models/schedule.model';

export function parseTimeToMinutes(time: string): number {
    const [hPart, mPart] = (time || '').split(':');
    const h = parseInt(hPart ?? '', 10);
    const m = parseInt(mPart ?? '0', 10);
    if (Number.isNaN(h)) return 0;
    return h * 60 + (Number.isNaN(m) ? 0 : m);
}

export function minutesToTime(totalMinutes: number): string {
    const mins = ((totalMinutes % 1440) + 1440) % 1440;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Soma horas ao horário (trata virada de meia-noite). */
export function addHoursToTime(startTime: string, hours: number): string {
    return minutesToTime(parseTimeToMinutes(startTime) + Math.round(hours * 60));
}

export function regimeEndTime(startTime: string | null | undefined, workHours: number | null | undefined): string | null {
    if (!startTime?.trim() || !workHours) return null;
    return addHoursToTime(startTime, workHours);
}

export function formatTimeHourShort(time?: string | null): string {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0] ?? '', 10);
    if (Number.isNaN(hour)) return '';
    return `${hour}h`;
}

export function formatTimeRange(start?: string | null, end?: string | null): string {
    const s = formatTimeHourShort(start);
    const e = formatTimeHourShort(end);
    return s && e ? `${s} - ${e}` : '';
}

export interface RegimeWindow {
    /** Índice do turno dentro do dia (0 = o do start_time cadastrado). */
    slot: number;
    start_time: string;
    end_time: string;
    /** 'Diurno'/'Noturno' quando o regime rende dois turnos; vazio se for único. */
    label: string;
}

type RegimeShape = Pick<RegimeModel, 'start_time' | 'work_hours' | 'covers_24h'>;

/** A carga fecha as 24h do dia sem sobra (6, 8, 12 ou 24 horas). */
export function tilesTheDay(workHours: number | null | undefined): boolean {
    const hours = Number(workHours) || 0;
    return hours > 0 && hours <= 24 && 24 % hours === 0;
}

/**
 * Quantos turnos deslocados o regime rende no dia. Só os regimes marcados como
 * `covers_24h` fatiam o dia inteiro: um 12x36 rende 8h-20h e 20h-8h. Os demais
 * são turno único, e o resto do dia fica sem cobertura — decisão da empresa.
 */
export function regimeWindowCount(regime: RegimeShape | null | undefined): number {
    if (!regime?.covers_24h || !tilesTheDay(regime.work_hours)) return 1;
    return 24 / Number(regime.work_hours);
}

export function regimeWindows(regime: RegimeShape | null | undefined): RegimeWindow[] {
    const start = regime?.start_time?.trim();
    const hours = Number(regime?.work_hours) || 0;
    if (!start || hours <= 0) return [];
    const count = regimeWindowCount(regime);
    const windows: RegimeWindow[] = [];
    for (let slot = 0; slot < count; slot++) {
        const start_time = addHoursToTime(start, hours * slot);
        windows.push({
            slot,
            start_time,
            end_time: addHoursToTime(start_time, hours),
            label: regimeWindowLabel(start_time, count, slot),
        });
    }
    return windows;
}

function regimeWindowLabel(startTime: string, count: number, slot: number): string {
    if (count <= 1) return '';
    if (count === 2) {
        const hour = parseTimeToMinutes(startTime) / 60;
        return hour >= 6 && hour < 18 ? 'Diurno' : 'Noturno';
    }
    return `Turno ${slot + 1}`;
}
